import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { inpatientAPI } from '../../../../services/api';
import './VetOrdersTab.css';

const VetOrdersTab = forwardRef(({ inpatientId, readOnly }, ref) => {
    const { t, i18n } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving', 'saved', null
    const saveTimeoutRef = useRef(null);
    const lastSavedOrdersRef = useRef(JSON.stringify([]));

    const fetchOrders = useCallback(async () => {
        if (!inpatientId) return;
        setLoading(true);
        try {
            const response = await inpatientAPI.getVetOrders(inpatientId);
            if (response.data?.status === 'success') {
                const data = response.data.data || [];
                setOrders(data);
                lastSavedOrdersRef.current = JSON.stringify(data.map(o => ({
                    id: o.id,
                    order_text: o.order_text,
                    is_fulfilled: o.is_fulfilled ? 1 : 0
                })));
            }
        } catch (error) {
            console.error('Error fetching vet orders:', error);
        } finally {
            setLoading(false);
        }
    }, [inpatientId]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const saveOrders = useCallback(async (isAutoSave = false) => {
        if (!inpatientId || readOnly) return;

        // Prepare current state for comparison
        const currentOrdersForSync = orders.map(order => {
            const o = {
                order_text: order.order_text,
                is_fulfilled: order.is_fulfilled ? 1 : 0,
                notes: order.notes || ''
            };
            if (order.id && !String(order.id).startsWith('new-')) {
                o.id = order.id;
            }
            return o;
        });

        const currentSyncStr = JSON.stringify(currentOrdersForSync.map(o => ({
            id: o.id,
            order_text: o.order_text,
            is_fulfilled: o.is_fulfilled
        })));

        // Skip if no changes since last save
        if (isAutoSave && currentSyncStr === lastSavedOrdersRef.current) {
            return;
        }

        if (isAutoSave) setAutoSaveStatus('saving');
        else setSaving(true);

        try {
            const payload = {
                inpatient_visit_id: parseInt(inpatientId),
                orders: currentOrdersForSync
            };
            const response = await inpatientAPI.bulkSyncVetOrders(payload);

            lastSavedOrdersRef.current = currentSyncStr;

            // Re-fetch to get real IDs and timestamps from server
            await fetchOrders();

            if (isAutoSave) {
                setAutoSaveStatus('saved');
                setTimeout(() => setAutoSaveStatus(null), 3000);
            }
            return response;
        } catch (error) {
            console.error('Error saving vet orders:', error);
            if (isAutoSave) setAutoSaveStatus('error');
            throw error; // Let parent handle notification
        } finally {
            if (!isAutoSave) setSaving(false);
        }
    }, [inpatientId, orders, readOnly, fetchOrders]);

    // Debounced Auto-save Effect
    useEffect(() => {
        if (readOnly || orders.length === 0) return;

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(() => {
            saveOrders(true).catch(() => { });
        }, 2000); // 2 second debounce

        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [orders, readOnly, saveOrders]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            await saveOrders();
        }
    }));

    const handleAddOrder = () => {
        if (readOnly) return;
        const newOrder = {
            id: `new-${Date.now()}`,
            order_text: '',
            is_fulfilled: false,
            notes: '',
            isNew: true,
            created_at: new Date().toISOString()
        };
        setOrders([...orders, newOrder]);
    };

    const handleUpdateOrder = (id, updates) => {
        if (readOnly) return;
        setOrders(orders.map(order => {
            if (order.id === id) {
                const updatedOrder = { ...order, ...updates };
                // If marking as fulfilled, set optimistic timestamp
                if (updates.is_fulfilled === true && !order.is_fulfilled) {
                    updatedOrder.fulfilled_at = new Date().toISOString();
                }
                // If unmarking, clear timestamp
                else if (updates.is_fulfilled === false && order.is_fulfilled) {
                    updatedOrder.fulfilled_at = null;
                }
                return updatedOrder;
            }
            return order;
        }));
    };

    const handleRemoveOrder = (id) => {
        if (readOnly) return;
        setOrders(orders.filter(order => order.id !== id));
    };

    const formatOrderDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                calendar: 'gregory',
            });
        } catch {
            return dateString;
        }
    };

    if (loading && orders.length === 0) {
        return <div className="vet-orders-loading">{t('common.loading')}</div>;
    }

    return (
        <div className="vet-orders-container tab-content-inner">
            {saving && <div className="saving-overlay"><div className="spinner"></div></div>}

            <div className="orders-header">
                <h3>{t('receptionInpatients.details.tabs.vetOrders', { defaultValue: 'Vet Orders' })}</h3>
                {autoSaveStatus && (
                    <div className={`auto-save-status ${autoSaveStatus}`}>
                        {autoSaveStatus === 'saving' && (
                            <><div className="spinner-mini"></div> {t('common.saving', { defaultValue: 'Saving...' })}</>
                        )}
                        {autoSaveStatus === 'saved' && (
                            <><i className="fas fa-check-circle"></i> {t('common.saved', { defaultValue: 'Saved' })}</>
                        )}
                        {autoSaveStatus === 'error' && (
                            <><i className="fas fa-exclamation-circle"></i> {t('common.saveError', { defaultValue: 'Save Error' })}</>
                        )}
                    </div>
                )}
            </div>

            <div className="orders-list">
                {orders.length === 0 && !readOnly && (
                    <div className="empty-orders">
                        <p>{t('receptionInpatients.details.vetOrders.noOrders', { defaultValue: 'No orders added yet.' })}</p>
                    </div>
                )}

                {orders.map((order, index) => (
                    <div
                        key={order.id}
                        className={`order-item ${order.is_fulfilled ? 'fulfilled' : ''}`}
                    >
                        <div className="order-number">#{index + 1}</div>

                        <div className="order-content">
                            <div className="order-main">
                                <div className="order-textarea-wrapper">
                                    <textarea
                                        value={order.order_text}
                                        onChange={(e) => handleUpdateOrder(order.id, { order_text: e.target.value })}
                                        placeholder={t('receptionInpatients.details.vetOrders.placeholder', { defaultValue: 'Enter order details...' })}
                                        disabled={readOnly || order.is_fulfilled}
                                        rows={3}
                                    />
                                </div>

                                {!readOnly && (
                                    <div className="order-actions">
                                        {/* Only show check and delete buttons if order is saved (has real ID, not temporary 'new-' ID) */}
                                        {order.id && !String(order.id).startsWith('new-') && (
                                            <>
                                                {!order.is_fulfilled && (
                                                    <button
                                                        className="btn-fulfill"
                                                        onClick={() => handleUpdateOrder(order.id, { is_fulfilled: true })}
                                                        title={t('receptionInpatients.details.vetOrders.markFulfilled', { defaultValue: 'Mark as Fulfilled' })}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                )}
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => handleRemoveOrder(order.id)}
                                                    title={t('common.delete')}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {order.created_at && (
                                <div className="order-date-wrapper created-date-wrapper">
                                    <span className="order-date created-date">
                                        <i className="far fa-clock"></i>
                                        {t('receptionInpatients.details.vetOrders.orderedAt', { defaultValue: 'Ordered at' })}: {formatOrderDate(order.created_at)}
                                    </span>
                                </div>
                            )}

                            {order.is_fulfilled && (
                                <div className="order-date-wrapper fulfillment-date-wrapper">
                                    <span className="order-date fulfilled-date">
                                        <i className="fas fa-check-circle"></i>
                                        {t('receptionInpatients.details.vetOrders.fulfilledAt', { defaultValue: 'Fulfilled at' })}: {formatOrderDate(order.fulfilled_at)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {!readOnly && (
                <button className="btn-add-order" onClick={handleAddOrder}>
                    <i className="fas fa-plus"></i>
                    {t('receptionInpatients.details.vetOrders.addOrder', { defaultValue: 'Add New Order' })}
                </button>
            )}
        </div>
    );
});

export default VetOrdersTab;
